/**
 * AR Vision Demo — eager registration of the Scene 7/8 voice-call tools.
 *
 * THROWAWAY DEMO CODE (GEN-2254 / T8). The recommendation + call UI lives on the
 * Collections Profile page (`ArOverdueCustomerDemo`), which also registers these
 * tools via `useArAgentPage` while mounted. But Scene 7's prompt may be typed
 * from ANY page (e.g. the AR summary) before the profile has ever mounted, so
 * the `ar_recommend_call` / `ar_place_call` tools must already be in the
 * demo-script registry. This module registers them eagerly (registry-only — it
 * does NOT touch the live agent module) so the interceptor can always find them.
 * Navigation to the overdue customer is the router-based
 * `ar_navigate_overdue_customer` in `useArDemoGlobalNavTools` (no page reload).
 *
 * Import this for its side effect from an always-mounted demo component.
 */

import { z } from 'zod';
import { registerArDemoTools } from './demo-script';
import { showCallRecommendation } from './voice-call-store';
import { runDemoVoiceCall } from './place-voice-call';

let registered = false;

/**
 * Register the Scene 7/8 tools into the demo-script registry. Idempotent. The
 * page-mounted `ArOverdueCustomerDemo` re-registers richer versions (with live
 * Vapi + router navigation) when it mounts; these are the always-available
 * fallbacks so the beats never silently no-op.
 */
export function registerVoiceCallTools(): void {
  if (registered) return;
  registered = true;

  registerArDemoTools('ar-voice-call-eager', [
    {
      name: 'ar_recommend_call',
      description:
        'Surface the AI recommendation to call the overdue customer about a credit hold.',
      awaitable: false,
      schema: {
        name: 'ar_recommend_call',
        description:
          'Surface the AI recommendation to call the overdue customer about a credit hold.',
        parameters: z.toJSONSchema(z.object({})) as Record<string, unknown>,
        awaitable: false,
      },
      execute: () => showCallRecommendation(),
    },
    {
      name: 'ar_place_call',
      description:
        'Place the outbound call to the overdue customer (live Vapi if configured, else scripted replay).',
      awaitable: true,
      schema: {
        name: 'ar_place_call',
        description:
          'Place the outbound call to the overdue customer (live Vapi if configured, else scripted replay).',
        parameters: z.toJSONSchema(z.object({})) as Record<string, unknown>,
        awaitable: true,
      },
      execute: () => runDemoVoiceCall(),
    },
  ]);
}
